import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { getIndicatorClassification, IndicatorEsfEnum } from "@/utils/indicatorsEsf";
import { ResumeIndicatorC6ByHealthTeamData } from "@/services/esus/indicator/esf/c6/getResumeIndicatorC6ByHealthTeam";

import { IndicatorEsfC6HealthTeamsFilters } from "./IndicatorEsfC6HealthTeamsFilters";
import { IndicatorEsfC6HealthTeamsClassificationTable } from "./IndicatorEsfC6HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC6HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorC6ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorC6ByHealthTeam: () => Promise<ResumeIndicatorC6ByHealthTeamData[]>;
};

export function IndicatorEsfC6HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorC6ByHealthTeam,
}: Readonly<IndicatorEsfC6HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);
	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorC6ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorC6AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Pontuações por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC6AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorC6ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe do atendimento": healthTeamClassification.healthTeam?.name ?? "",
					"Total de idosos": healthTeamClassification.totalElderlyCitizensInPeriod,
					Pontuação: healthTeamClassification.totalElderlyCitizensInPeriodScore,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalElderlyCitizensInPeriodScore,
						healthTeamClassification.maxElderlyCitizensInPeriodScore
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsfEnum.C6,
						calculatePercentage(
							healthTeamClassification.totalElderlyCitizensInPeriodScore,
							healthTeamClassification.maxElderlyCitizensInPeriodScore
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC6HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC6HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
